import {
  Alert,
  Button,
  ButtonGroup,
  Card,
  Col,
  Container,
  Modal,
  Row,
  Spinner,
  Table,
} from 'react-bootstrap';
import type { BillingInterval, Plan } from '../types/types';
import mostPopularBadge from '../assets/MostPopularBadge.png';
import SupplierPlanImage from '../assets/SupplierPlanImage.jpg';
import ManagerPlanImage from '../assets/ManagerPlanImage.jpg';
import { useEffect, useState } from 'react';
import { tracking } from '../util/tracking';
import { supabase } from '../lib/supabase';

type PlanSelectProps = {
  onSelect: (planId: string, billingInterval: BillingInterval) => void;
  plans?: Plan[];
  currentPlanTier?: number | null;
};

type DefaultTemplateListItem = {
  id: string;
  title: string;
};

// const freePlanId = '82a8b5f1-1959-4fc3-a37a-f6b8f16d2423';

const featureLabels: Record<string, string> = {
  dppProducts: 'Create up to {value} DPPs',
  displayBadge: 'Embed badge on your website',
  publishReports: 'Publish and share reports',
  selfAssessment: 'Self assessments',
  aiCompliancePlan: 'AI assisted compliance plan',
  questionnaireBuilder: 'Questionnaire Builder',
  managedSuppliers: 'Manage up to {value} suppliers',
  inviteSuppliers: 'Invite suppliers',
  importSuppliers: 'Import suppliers',
  manageSuppliers: 'Manage Suppliers',
  sendAssessments: 'Send Assessments',
  publishProfile: 'Publish supplier profile',
  supplyChainMapping: 'Supply chain mapping',
};

const templateLabels: Record<string, string> = {
  supplier_esg_report: 'ESG',
  supplier_lksg_report: 'LkSG (Germany)',
  supplier_csddd_report: 'CSDDD',
  supplier_fdv_report: 'FDV (France)',
  supplier_csrd_report: 'CSRD',
  esrs_vsme_2026: 'ESRS (VSME 2026)',
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function formatFeatures(features: any): React.ReactNode[] {
  const result: React.ReactNode[] = [];

  Object.entries(features).forEach(([key, value]) => {
    if (typeof value === 'boolean' && value) {
      if (featureLabels[key]) result.push(featureLabels[key]);
    } else if (typeof value === 'number') {
      if (featureLabels[key]) {
        result.push(featureLabels[key].replace('{value}', String(value)));
      }
    } else if (key === 'selfAssessmentTemplateKeys' && Array.isArray(value)) {
      const templates = value
        .map((templateKey: string) => templateLabels[templateKey])
        .filter(Boolean);
      if (templates.length > 0) {
        result.push(
          <>
            Self assessment questionnaires:
            <br />
            <strong>{templates.join(', ')}</strong>
          </>,
        );
      }
    }
  });

  return result;
}

function getPlanPrice(plan: Plan, billingInterval: BillingInterval) {
  if (plan.tier === 5) return null;

  if (billingInterval === 'yearly' && plan.price_yearly != null) {
    return Number(plan.price_yearly) / 12;
  }

  if (plan.price != null) {
    return Number(plan.price);
  }

  return null;
}

function formatPlanPrice(value: number | null) {
  if (value == null || Number.isNaN(value)) {
    return 'Custom';
  }

  const hasFractions = !Number.isInteger(value);
  return new Intl.NumberFormat(undefined, {
    minimumFractionDigits: hasFractions ? 2 : 0,
    maximumFractionDigits: hasFractions ? 2 : 0,
  }).format(value);
}

export default function PlanSelect({
  onSelect,
  plans = [],
  currentPlanTier,
}: PlanSelectProps) {
  const [planType, setPlanType] = useState<string>('basic');
  const [billingInterval, setBillingInterval] =
    useState<BillingInterval>('yearly');
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [defaultTemplates, setDefaultTemplates] = useState<
    DefaultTemplateListItem[]
  >([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [templatesError, setTemplatesError] = useState<string | null>(null);

  // Show the other plan as most popular if user is already on the most popular one
  const most_popular_plan_tier = currentPlanTier === 2 ? 0 : 2;

  useEffect(() => {
    if (!showTemplateModal || defaultTemplates.length > 0) {
      return;
    }

    let isMounted = true;

    const fetchDefaultTemplates = async () => {
      setIsLoadingTemplates(true);
      setTemplatesError(null);

      const { data, error } = await supabase
        .from('default_template_list')
        .select('id, title')
        .order('title', { ascending: true });

      if (!isMounted) {
        return;
      }

      if (error) {
        setTemplatesError('Unable to load questionnaire templates.');
        setDefaultTemplates([]);
      } else {
        setDefaultTemplates(data ?? []);
      }

      setIsLoadingTemplates(false);
    };

    void fetchDefaultTemplates();

    return () => {
      isMounted = false;
    };
  }, [defaultTemplates.length, showTemplateModal]);

  return (
    <Container>
      {!planType ? (
        <Row className="gy-4 justify-content-center">
          <Col xs={12} md={3}>
            <Card className={`h-100 shadow-sm border-1 rounded-3`}>
              <Card.Img
                variant="top"
                src={SupplierPlanImage}
                alt="Supplier"
                style={{ height: '200px', objectFit: 'cover' }}
              />
              <Card.Body className="d-flex flex-column">
                <p className="text-center text-muted">
                  I want to manage my own compliance, create reports and share
                  them with my buyers and partners.
                </p>
                <Button
                  variant="primary"
                  size="lg"
                  className="w-100 mt-auto"
                  onClick={() => {
                    tracking.log('UI', 'plan_type_supplier', {
                      planType: 'basic',
                    });
                    setPlanType('basic');
                  }}
                >
                  Supplier Plans
                </Button>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={12} md={3}>
            <Card className={`h-100 shadow-sm border-1 rounded-3`}>
              <Card.Img
                variant="top"
                src={ManagerPlanImage}
                alt="Compliance Manager"
                style={{ height: '200px', objectFit: 'cover' }}
              />
              <Card.Body className="d-flex flex-column">
                <p className="text-center text-muted">
                  I want to manage my suppliers' compliance and keep track of
                  their performance and analyze risks.
                </p>
                <Button
                  variant="primary"
                  size="lg"
                  className="w-100 mt-auto"
                  onClick={() => {
                    tracking.log('UI', 'plan_type_manager', {
                      planType: 'admin',
                    });
                    setPlanType('admin');
                  }}
                >
                  Compliance Manager Plans
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      ) : (
        <>
          <div className="d-flex flex-column align-items-center mt-3">
            <div className="text-uppercase small fw-semibold text-muted mb-2">
              Billing cycle
            </div>
            <ButtonGroup
              aria-label="Billing cycle"
              className="border rounded-pill p-1 bg-light flex-wrap"
            >
              <Button
                variant={billingInterval === 'yearly' ? 'primary' : 'light'}
                className="rounded-pill px-4"
                onClick={() => {
                  setBillingInterval('yearly');
                  tracking.log('UI', 'billing_interval_changed', {
                    planType,
                    billingInterval: 'yearly',
                  });
                }}
              >
                Yearly
              </Button>
              <Button
                variant={billingInterval === 'monthly' ? 'primary' : 'light'}
                className="rounded-pill px-4"
                onClick={() => {
                  setBillingInterval('monthly');
                  tracking.log('UI', 'billing_interval_changed', {
                    planType,
                    billingInterval: 'monthly',
                  });
                }}
              >
                Monthly
              </Button>
            </ButtonGroup>
            <p className="small text-muted text-center mt-2 mb-0">
              Yearly plans are billed annually. Displayed price is the monthly
              equivalent.
            </p>
          </div>
          <Row className="gy-4 justify-content-center mt-4">
            {plans
              .filter((p) => {
                if (currentPlanTier && currentPlanTier !== 1 && p.tier === 1) {
                  return false;
                }
                return p.type === planType;
              })
              .map((plan) => (
                <Col
                  key={plan.name}
                  xs={12}
                  md={6}
                  xl
                  className="plan-select-col"
                >
                  <Card
                    className={`h-100 shadow-sm ${
                      plan.tier === most_popular_plan_tier
                        ? 'border-primary border-3 rounded-3'
                        : ''
                    }`}
                    style={
                      plan.tier === most_popular_plan_tier
                        ? { transform: 'scale(1.05)' }
                        : {}
                    }
                  >
                    <Card.Header
                      className={`text-center fw-bold${
                        plan.tier === most_popular_plan_tier
                          ? ' bg-primary text-white'
                          : ' bg-light'
                      }`}
                    >
                      {plan.name}
                      {plan.tier === most_popular_plan_tier && (
                        <img
                          src={mostPopularBadge}
                          alt="Most Popular"
                          width="40"
                          className="most-popular-badge"
                        />
                      )}
                    </Card.Header>
                    <Card.Body className="d-flex flex-column">
                      {plan.tier === 5 ? (
                        <p className="text-center text-muted">
                          Need a custom plan?
                        </p>
                      ) : (
                        <p className="text-center mb-4">
                          <span className="display-6 fw-bold">
                            {formatPlanPrice(
                              getPlanPrice(plan, billingInterval),
                            )}
                          </span>
                          {plan.currency}/m
                          {billingInterval === 'yearly' && (
                            <span className="d-block small text-muted mt-1">
                              billed annually
                            </span>
                          )}
                        </p>
                      )}
                      <ul className="list-unstyled mb-4 flex-grow-1">
                        {plan.features &&
                          formatFeatures(plan.features).map((label, i) => (
                            <li key={i} className="text-start">
                              <i
                                className="bi bi-check-circle-fill me-2"
                                style={{ color: '#2a245a' }}
                              ></i>
                              {label}
                            </li>
                          ))}
                        {plan.tier !== 1 && (
                          <li className="text-start">
                            <Button
                              variant="link"
                              size="sm"
                              onClick={() => {
                                setShowTemplateModal(true);
                                tracking.log('UI', 'template_modal_opened', {
                                  planType,
                                  planId: plan.id,
                                  planTier: plan.tier,
                                });
                              }}
                            >
                              See all included questionnaires
                            </Button>
                          </li>
                        )}
                      </ul>
                      {plan.tier === 5 ? (
                        <Button
                          variant="primary"
                          size="lg"
                          className="w-100 mt-auto"
                          onClick={() => {
                            tracking.log('UI', 'plan_contact', {
                              planType: planType,
                              planId: plan.id,
                              planTier: plan.tier,
                            });
                            window.location.href =
                              'mailto:info@supplypassport.co';
                          }}
                        >
                          Contact Us
                        </Button>
                      ) : (
                        <Button
                          variant={
                            plan.tier === most_popular_plan_tier
                              ? 'primary'
                              : 'outline-primary'
                          }
                          size="lg"
                          disabled={currentPlanTier === plan.tier}
                          className="w-100 mt-auto"
                          onClick={() => {
                            tracking.log('UI', 'plan_select', {
                              planType: planType,
                              planId: plan.id,
                              planTier: plan.tier,
                              billingInterval,
                            });
                            onSelect(plan.id, billingInterval);
                          }}
                        >
                          {currentPlanTier === plan.tier
                            ? 'Current Plan'
                            : 'Select Plan'}
                        </Button>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              ))}
          </Row>
        </>
      )}
      <Modal
        show={showTemplateModal}
        onHide={() => setShowTemplateModal(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title as="h2">Default questionnaire templates</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {isLoadingTemplates ? (
            <div className="d-flex justify-content-center py-4">
              <Spinner animation="border" role="status" variant="primary" />
            </div>
          ) : templatesError ? (
            <Alert variant="danger" className="mb-0">
              {templatesError}
            </Alert>
          ) : (
            <div
              style={{
                minHeight: '240px',
                maxHeight: '420px',
                overflowY: 'auto',
              }}
            >
              <Table responsive className="mb-0">
                <tbody>
                  {defaultTemplates.length > 0 ? (
                    defaultTemplates.map((template) => (
                      <tr key={template.id}>
                        <td>{template.title}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={2} className="text-center text-muted">
                        No default templates found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </Container>
  );
}
